import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User, Business, LoginCredentials, SignupData, Permission } from '../types';
import { authService } from '../services/auth.service';

interface AuthState {
  token: string | null;
  user: User | null;
  business: Business | null;
  permissions: Permission[];
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => void;
  validateSession: () => Promise<boolean>;
  refreshAccessToken: () => Promise<boolean>;
  clearError: () => void;
  setUser: (user: User) => void;
  setBusiness: (business: Business) => void;

  // Permission helpers
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      business: null,
      permissions: [],
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.login(credentials);
          set({
            token: response.token,
            user: response.user,
            business: response.business,
            permissions: response.permissions || [],
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      signup: async (data: SignupData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.signup(data);
          set({
            token: response.token,
            user: response.user,
            business: response.business,
            permissions: response.permissions || [],
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Signup failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      logout: () => {
        // Clear auth service tokens
        authService.clearTokens();

        set({
          token: null,
          user: null,
          business: null,
          permissions: [],
          isAuthenticated: false,
          error: null,
        });
        // Clear persisted auth storage
        localStorage.removeItem('auth-storage');
        // Clear sysadmin storage
        localStorage.removeItem('sysadmin-storage');
      },

      validateSession: async () => {
        const { token } = get();
        if (!token) {
          set({ isAuthenticated: false });
          return false;
        }

        try {
          const response = await authService.validateSession();
          set({
            user: response.user,
            business: response.business,
            permissions: response.permissions || [],
            isAuthenticated: true,
          });
          return true;
        } catch {
          // Try to refresh the token before giving up
          const refreshed = await get().refreshAccessToken();
          if (!refreshed) {
            set({
              token: null,
              user: null,
              business: null,
              permissions: [],
              isAuthenticated: false,
            });
          }
          return refreshed;
        }
      },

      refreshAccessToken: async () => {
        try {
          const response = await authService.refreshToken();
          set({
            token: response.token,
            user: response.user,
            permissions: response.permissions || [],
            isAuthenticated: true,
          });
          return true;
        } catch {
          // Refresh failed - clear everything
          authService.clearTokens();
          set({
            token: null,
            user: null,
            business: null,
            permissions: [],
            isAuthenticated: false,
          });
          return false;
        }
      },

      clearError: () => set({ error: null }),

      setUser: (user: User) => set({ user }),

      setBusiness: (business: Business) => set({ business }),

      // Permission helpers
      hasPermission: (permission: Permission) => {
        const { permissions } = get();
        return permissions.includes(permission);
      },

      hasAnyPermission: (perms: Permission[]) => {
        const { permissions } = get();
        return perms.some(p => permissions.includes(p));
      },

      hasAllPermissions: (perms: Permission[]) => {
        const { permissions } = get();
        return perms.every(p => permissions.includes(p));
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        business: state.business,
        permissions: state.permissions,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
